import { createAsyncThunk } from "@reduxjs/toolkit"

import { IShippingAddressTo } from "../../../model/interface/IShippingAddress"
import { ClinicalSettings } from "../../../model/User"
import { RootState } from "../../store"
import store from "../../store"

import * as userApiService from "./user.service"
import { createUpdateOrgShipping, deleteorgShipping } from "./user.service"
import {
  AccountActivateParams,
  IForgotPassword,
  IResetPassword,
  IUpdateCardPayload,
  IUpdatePassword,
  IUpdateUser,
  IUserPreferences,
} from "./user.type"

export const fetchUserPreferences = createAsyncThunk(
  "userService/fetchUserPreferences",
  async (arg, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    if (userState.preferences) {
      return userState.preferences
    }
    try {
      const orgId = userState.isAllowToViewAllOrg
        ? userState.authorized_roles[0].org_id
        : userState.current_orgId
      const userId = userState.user_id
      const response = await userApiService.getUserPreferences(orgId, userId)
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const updateUserPreferences = createAsyncThunk(
  "userService/updateUserPreferences",
  async (payload: IUserPreferences, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.isAllowToViewAllOrg
        ? userState.authorized_roles[0].org_id
        : userState.current_orgId
      const userId = userState.user_id
      const response = await userApiService.updateUserPreferences(
        orgId,
        userId,
        payload,
      )
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const fetchUserProfile = createAsyncThunk(
  "userService/fetchUserProfile",
  async (arg, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.isAllowToViewAllOrg
        ? userState.authorized_roles[0].org_id
        : userState.current_orgId
      const userId = userState.user_id
      const response = await userApiService.getUserProfile(orgId, userId)
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const updateUserProfile = createAsyncThunk(
  "userService/updateUserProfile",
  async (payload: IUpdateUser, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.isAllowToViewAllOrg
        ? userState.authorized_roles[0].org_id
        : userState.current_orgId
      const userId = userState.user_id
      const response = await userApiService.updateUserProfile(
        orgId,
        userId,
        payload,
      )
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const updatePassword = createAsyncThunk(
  "userService/updatePassword",
  async (payload: IUpdatePassword, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.isAllowToViewAllOrg
        ? userState.authorized_roles[0].org_id
        : userState.current_orgId
      const userId = userState.user_id
      const response = await userApiService.updatePasswordService(
        { ...payload, okta_user_id: userState.uid },
        userId,
        orgId,
      )
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const resetPassword = createAsyncThunk(
  "userService/resetPassword",
  async (payload: IResetPassword, { rejectWithValue }) => {
    try {
      const response = await userApiService.resetPasswordService({ ...payload })
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const forgotPassword = createAsyncThunk(
  "userService/forgotPassword",
  async (payload: IForgotPassword, { rejectWithValue }) => {
    try {
      const response = await userApiService.forgotPasswordService({
        ...payload,
      })
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const fetchClinicalSettings = createAsyncThunk(
  "userService/fetchClinicalSettings",
  async (_, { rejectWithValue, getState }) => {
    const userState = (getState() as RootState).userService.user
    try {
      const orgId = userState.current_orgId
      const res = await userApiService.getClinicalSettting(orgId)
      if (res.response_code === 200) {
        const { result } = res
        const clinicalSettingsData = result.clinical_settings || {}
        return new ClinicalSettings().Deserialize(clinicalSettingsData)
      }
      return new ClinicalSettings().Deserialize({})
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const updateClinicalSettings = createAsyncThunk(
  "userService/updateClinicalSetttings",
  async (payload: unknown, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.current_orgId
      const response = await userApiService.saveClinicalSettting(orgId, payload)
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const fetchCountryList = createAsyncThunk(
  "userService/fetchCountryList",
  async (arg, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.current_orgId
      const response = await userApiService.getCountryList(orgId)
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const fetchStateLists = createAsyncThunk(
  "userService/fetchStateList",
  async (
    { countryid }: { countryid: number },
    { rejectWithValue, getState },
  ) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.current_orgId
      const response = await userApiService.getStatesList(orgId, countryid)
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const saveCreditCard = createAsyncThunk(
  "userService/saveCreditCard",
  async (payload: IUpdateCardPayload, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.current_orgId
      const res = await userApiService.addCardDetails(orgId, payload)

      return res
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const updateCreditCard = createAsyncThunk(
  "userService/updateCreditCard",
  async (payload: IUpdateCardPayload, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.current_orgId
      const res = await userApiService.updateCardDetails(orgId, payload)

      return res
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const getCreditCardDetails = createAsyncThunk(
  "userService/getCreditCardDetails",
  async (_, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.current_orgId
      const response = await userApiService.getCardDetails(orgId)
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const activateUser = createAsyncThunk(
  "userService/activateUser",
  async (payload: AccountActivateParams, { rejectWithValue }) => {
    try {
      return await userApiService.activateUser(payload)
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchIfuDoc = createAsyncThunk(
  "userService/FetchIfuDocument",
  async (arg, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.current_orgId
      const userId = userState.user_id
      const response = await userApiService.getIfuDocument(orgId, userId)
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const fetchReleaseNotes = createAsyncThunk(
  "userService/fetchReleaseNotes",
  async (versionKey: string, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.current_orgId
      const userId = userState.user_id
      const response = await userApiService.getReleaseNotes(orgId, userId, versionKey)
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const fetchHelpDocuments = createAsyncThunk(
  "userService/fetchHelpDocuments",
  async (fileName: string, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.current_orgId
      const userId = userState.user_id
      const response = await userApiService.getHelpDocuments(
        orgId,
        userId,
        fileName,
      )
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const getAccountDetail = createAsyncThunk(
  "userService/getAccountDetail",
  async () => {
    const orgId = store.getState()?.userService?.user?.current_orgId
    try {
      return await userApiService.getAccountDetails(orgId)
    } catch (err) {
      return err
    }
  },
)

export const getPermissions = createAsyncThunk(
  "userService/getPermissions",
  async (_, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.isAllowToViewAllOrg
        ? userState.authorized_roles[0].org_id
        : userState.current_orgId
      return await userApiService.getUserPermissions(orgId, userState.user_id)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const getUDAppExe = createAsyncThunk(
  "userService/getUDAppExe",
  async (version: string, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.current_orgId
      return await userApiService.getUDApp(orgId, version)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const getSystemMessage = createAsyncThunk(
  "userService/getSystemMessage",
  async (arg, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.current_orgId
      return await userApiService.getSystemMessages(orgId)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const fetchShippingAddressList = createAsyncThunk(
  "userService/fetchShippingAddressList",
  async (_, { rejectWithValue }) => {
    try {
      const orgId = store.getState().userService.user.current_orgId
      const response = await userApiService.getShippingAddress(orgId)
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const createUpdateOrgShippingAddress = createAsyncThunk(
  "userService/createUpdateOrgShippingAddress",
  async (payload: IShippingAddressTo, { rejectWithValue }) => {
    const orgId = store.getState().userService.user.current_orgId
    try {
      return await createUpdateOrgShipping(orgId, payload)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const deleteShippingAddress = createAsyncThunk(
  "userService/deleteShippingAddress",
  async (shippingId: number, { rejectWithValue, getState }) => {
    const orgId = store.getState().userService.user.current_orgId
    try {
      return await deleteorgShipping(orgId, shippingId)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const fetchAddressSuggestions = createAsyncThunk(
  "userService/fetchAddressSuggestions",
  async (payload: string, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.current_orgId
      const userId = userState.user_id

      const response = await userApiService.getAddressSuggestions(
        orgId,
        userId,
        payload,
      )
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const fetchWorkflowSettings = createAsyncThunk(
  "userService/fetchWorkflowSettings",
  async (_, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.current_orgId
      const response = await userApiService.fetchWorkflowSettingsApi(orgId)
      return response
    } catch (err){
      return rejectWithValue(err)
    }
  }
)

export const updateWorkflowSettings = createAsyncThunk(
  "userService/updateWorkflowSettings",
  async (payload:{
    ShowWorkflow: Number,
    phase:{
      id: Number,
      value: string
    }[]
  }, { rejectWithValue, getState }) => {
    const userState = getState().userService.user
    try {
      const orgId = userState.current_orgId
      const response = await userApiService.updateWorkflowSettingsApi(orgId, payload)
      return response
    } catch (err){
      return rejectWithValue(err)
    }
  }
)

export const fetchCaseDispositions = createAsyncThunk(
  "userService/fetchCaseDispositions",
  async (_, { rejectWithValue, getState }) => {
    const userState = (getState() as RootState).userService.user
    try {
      const orgId = userState.current_orgId
      const response = await userApiService.fetchCaseDispositionsApi(orgId)
      if(response.response_code === 200){

        const newResponse = response?.result?.map(caseDispositionObj => {
          let configObj = JSON.parse(caseDispositionObj.config)
          return {
            id: caseDispositionObj.type_i_id,
            key: caseDispositionObj.type_s_id,
            isDisableDropdownShow: configObj.isDisableDropdownShow || false,
            type: caseDispositionObj.type,
            displayText: configObj.displayText,
            style: configObj.style
          }
        })

        return newResponse

      }else{
        rejectWithValue('Failed to fetch case dispositions')
      }
    } catch (err){
      console.log('err', err)
      return rejectWithValue(err)
    }
  }
)